import { ChevronsUpDown, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@repo/utils";
import type { ColumnDef } from "./data-table.types.js";
import { Button } from "../button/button.js";

type SortConfig<T> = {
  key: keyof T;
  direction: "asc" | "desc";
} | null;

const sortIcons = {
  asc: <ChevronUp className="text-gxe-primary h-4 w-4" />,
  desc: <ChevronDown className="text-gxe-primary h-4 w-4" />,
  none: <ChevronsUpDown className="h-4 w-4" />,
} as const;

type Props<T> = {
  columns: ColumnDef<T>[];
  sortConfig: SortConfig<T>;
  onSort: (key: keyof T) => void;
  headerClassName?: string;
  showActionsColumn: boolean;
  actionsHeaderContent?: React.ReactNode;
  hasActions: boolean;
  hasSearchableColumns: boolean;
};

export const DataTableHeader = <T extends Record<string, unknown>>({
  columns,
  sortConfig,
  onSort,
  headerClassName,
  showActionsColumn,
  actionsHeaderContent,
  hasActions,
  hasSearchableColumns,
}: Props<T>) => {
  return (
    <thead className={cn("flex px-4 text-sm font-semibold", headerClassName)}>
      <tr className="flex w-full min-w-0 items-center gap-4 py-3">
        {columns.map((column) => (
          <th
            key={String(column.key)}
            className={cn("text-primary flex min-w-0 items-center gap-2 text-left", {
              "flex-1": !column.width,
              [column.width ?? ""]: !!column.width,
            })}
          >
            <div className="flex min-w-0 items-center">
              <span className="text-gxe-primary truncate">{column.header}</span>
              {column.sortable && (
                <Button
                  variant="ghost"
                  size="icon_sm"
                  onClick={() => onSort(column.key)}
                  className="hover:text-gxe-gray flex-shrink-0 text-gray-400 hover:bg-transparent"
                >
                  {sortIcons[sortConfig?.key === column.key ? sortConfig.direction : "none"]}
                </Button>
              )}
            </div>
          </th>
        ))}
        {showActionsColumn && (
          <th className="bg-gxe-bg-light sticky right-0 z-10 flex min-w-0 flex-shrink-0 items-center justify-end pl-2">
            <div className="flex-1 items-center gap-2">
              {actionsHeaderContent}
              {hasActions && !hasSearchableColumns && <div className="w-10" />}
            </div>
          </th>
        )}
      </tr>
    </thead>
  );
};

DataTableHeader.displayName = "DataTableHeader";
